import { useMemo, useCallback, createContext, useEffect, useReducer } from 'react';
import { useSnackbar } from 'notistack';
import { Ability } from '@casl/ability';
import PropTypes from 'prop-types';
import useLocales from '../hooks/useLocales';
// utils
import axios from '../utils/axios';
import { IconButtonAnimate } from '../components/animate';
import Iconify from '../components/Iconify';
import { setSession, isValidSession } from '../utils/jwt';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  ability: new Ability([]),
  user: null,
  authParams: null
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, authParams, ability } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user: user || null,
      authParams: authParams || null,
      ability: ability || new Ability([])
    };
  },
  LOGIN: (state, action) => {
    const { user, authParams, ability } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      isInitialized: true,
      user,
      authParams,
      ability
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    isInitialized: true,
    user: null,
    authParams: null,
    ability: new Ability([])
  })
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve()
});

AuthProvider.propTypes = {
  children: PropTypes.node
};

const extractErrorMessage = (error, defaultMessage = 'An unexpected error occurred') => {
  if (!error) return defaultMessage;
  if (Array.isArray(error.errors)) return error.errors.join('. ');
  if (typeof error.errors === 'string') return error.errors;
  if (typeof error.error === 'string') return error.error;
  if (typeof error.message === 'string') return error.message;
  if (typeof error === 'string') return error;
  if (typeof error === 'object') {
    try {
      const values = Object.values(error).flat();
      if (values.length > 0 && typeof values[0] === 'string') {
        return values.join('. ');
      }
    } catch {
      // ignore
    }
  }
  return defaultMessage;
};

function AuthProvider({ children }) {
  const { translate: t } = useLocales();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const initialize = useCallback(async () => {
    try {
      const rawAuthParams = window.localStorage.getItem('authParams');
      if (!rawAuthParams) {
        setSession(null);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            authParams: null,
            ability: new Ability([])
          }
        });
        return;
      }

      let authParams = null;
      try {
        authParams = JSON.parse(rawAuthParams);
      } catch (e) {
        console.error('Failed to parse authParams from localStorage:', e);
        setSession(null);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            authParams: null,
            ability: new Ability([])
          }
        });
        return;
      }

      if (!isValidSession(authParams)) {
        setSession(null);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            authParams: null,
            ability: new Ability([])
          }
        });
        return;
      }

      setSession(authParams);

      try {
        const response = await axios.post('/api/users/me');
        if ([200, 201].includes(response.status)) {
          const userData = response.data;
          const permissions = authParams?.userInfo?.permissions || userData?.permissions || [];
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user: userData,
              authParams,
              ability: new Ability(permissions)
            }
          });
          if (userData && authParams) {
            authParams.userInfo = userData;
            setSession(authParams);
          }
        } else {
          throw new Error(`Unexpected status code: ${response.status}`);
        }
      } catch (error) {
        console.warn('Session verification failed on initialize:', error);
        setSession(null);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            authParams: null,
            ability: new Ability([])
          }
        });
      }
    } catch (err) {
      console.error('Unexpected error in AuthProvider initialize:', err);
      setSession(null);
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null,
          authParams: null,
          ability: new Ability([])
        }
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const prossessedResponse = useCallback(
    (response) => {
      if (!response) return [];
      if ([401].includes(response.status)) {
        enqueueSnackbar(response.message || t('APIcompound.unauthorized'), {
          variant: response.code || 'warning',
          action: (key) => (
            <IconButtonAnimate size="small" onClick={() => closeSnackbar(key)}>
              <Iconify icon={'eva:close-fill'} />
            </IconButtonAnimate>
          )
        });

        setSession(null);
        dispatch({ type: 'LOGOUT' });

        return [];
      }
      if ([201, 200, 422].includes(response.status)) {
        if (response.message) {
          enqueueSnackbar(response.message, {
            variant: response.code || 'info',
            action: (key) => (
              <IconButtonAnimate size="small" onClick={() => closeSnackbar(key)}>
                <Iconify icon={'eva:close-fill'} />
              </IconButtonAnimate>
            )
          });
        }
      }
      return response;
    },
    [closeSnackbar, enqueueSnackbar, t]
  );

  const logIn = useCallback(
    async (params) => {
      try {
        const response = await axios.post('/api/users/sign_in', params);
        if ([200, 201].includes(response.status)) {
          const authParams = {
            tokens: {
              accessToken: response.headers['access-token'],
              tokenType: response.headers['token-type'] || 'Bearer',
              client: response.headers.client,
              expiry: response.headers.expiry,
              uid: response.headers.uid
            },
            userInfo: response.data.data
          };
          setSession(authParams);
          prossessedResponse({ status: 200, message: t('APIcompound.connectSuccess'), code: 'success' });
          dispatch({
            type: 'LOGIN',
            payload: {
              user: response.data.data,
              authParams,
              ability: new Ability(authParams?.userInfo?.permissions || [])
            }
          });
        }
      } catch (error) {
        const message = extractErrorMessage(error);
        enqueueSnackbar(message, {
          variant: 'error',
          action: (key) => (
            <IconButtonAnimate size="small" onClick={() => closeSnackbar(key)}>
              <Iconify icon={'eva:close-fill'} />
            </IconButtonAnimate>
          )
        });
      }
    },
    [closeSnackbar, enqueueSnackbar, prossessedResponse, t]
  );

  const logOut = useCallback(async () => {
    try {
      await axios.delete('/api/users/sign_out');
      prossessedResponse({ status: 200, message: t('APIcompound.disconnectSuccess'), code: 'info' });
    } catch (error) {
      console.warn('Sign out request failed:', error);
    } finally {
      setSession(null);
      dispatch({ type: 'LOGOUT' });
    }
  }, [prossessedResponse, t]);

  const register = useCallback(
    async (params) => {
      try {
        const response = await axios.post('/api/users', params);
        if ([200, 201].includes(response.status)) {
          prossessedResponse({ status: 200, message: t('APIcompound.registerUserSuccess'), code: 'success' });
        }
        return true;
      } catch (error) {
        const message = extractErrorMessage(error);
        enqueueSnackbar(`(${params.email}) ${message}`, {
          variant: 'error',
          action: (key) => (
            <IconButtonAnimate size="small" onClick={() => closeSnackbar(key)}>
              <Iconify icon={'eva:close-fill'} />
            </IconButtonAnimate>
          )
        });
        return false;
      }
    },
    [closeSnackbar, enqueueSnackbar, prossessedResponse, t]
  );

  const confirm = useCallback(
    async (params) => {
      try {
        const response = await axios.get(`/api/users/confirmation?confirmation_token=${params.token}`);
        if ([200, 201].includes(response.status)) {
          prossessedResponse({ status: 200, message: t('APIcompound.confirmUserSuccess'), code: 'success' });
        }
        return true;
      } catch (error) {
        console.warn('Confirmation error:', error);
        const message = extractErrorMessage(error);
        enqueueSnackbar(message, {
          variant: 'error',
          action: (key) => (
            <IconButtonAnimate size="small" onClick={() => closeSnackbar(key)}>
              <Iconify icon={'eva:close-fill'} />
            </IconButtonAnimate>
          )
        });
        return false;
      }
    },
    [closeSnackbar, enqueueSnackbar, prossessedResponse, t]
  );

  const validateToken = useCallback(
    async (params) => {
      try {
        const response = await axios.get(`/api/users/password/edit?reset_password_token=${params.token}`);
        if ([200, 201].includes(response.status)) {
          prossessedResponse({ status: 200, message: t('APIcompound.resetPasswordFormSuccess'), code: 'success' });
        }
        return response.data;
      } catch (error) {
        const message = extractErrorMessage(error);
        enqueueSnackbar(message, {
          variant: 'error',
          action: (key) => (
            <IconButtonAnimate size="small" onClick={() => closeSnackbar(key)}>
              <Iconify icon={'eva:close-fill'} />
            </IconButtonAnimate>
          )
        });
        return error?.errors || error;
      }
    },
    [closeSnackbar, enqueueSnackbar, prossessedResponse, t]
  );

  const passwordResetRequest = useCallback(
    async (params) => {
      try {
        const response = await axios.post('/api/users/password', params);
        if ([200, 201].includes(response.status)) {
          prossessedResponse({ status: 200, message: t('APIcompound.passwordResetSuccess'), code: 'success' });
        }
        return true;
      } catch (error) {
        const message = extractErrorMessage(error);
        enqueueSnackbar(message, {
          variant: 'error',
          action: (key) => (
            <IconButtonAnimate size="small" onClick={() => closeSnackbar(key)}>
              <Iconify icon={'eva:close-fill'} />
            </IconButtonAnimate>
          )
        });
        return false;
      }
    },
    [closeSnackbar, enqueueSnackbar, prossessedResponse, t]
  );

  const updatePassword = useCallback(
    async (params) => {
      try {
        const headers = params.headers || {};
        const response = await axios.put('/api/users/password', params.auth, {
          headers: {
            Authorization: `Bearer ${headers['token-type'] || 'Bearer'}`,
            'access-token': headers['access-token'],
            'token-type': headers['token-type'] || 'Bearer',
            client: headers.client,
            expiry: headers.expiry,
            uid: headers.uid
          }
        });
        if ([200, 201].includes(response.status)) {
          prossessedResponse({ status: 200, message: t('APIcompound.passwordUpdateSuccess'), code: 'success' });
        }
        return true;
      } catch (error) {
        const message = extractErrorMessage(error);
        enqueueSnackbar(message, {
          variant: 'error',
          action: (key) => (
            <IconButtonAnimate size="small" onClick={() => closeSnackbar(key)}>
              <Iconify icon={'eva:close-fill'} />
            </IconButtonAnimate>
          )
        });
        return false;
      }
    },
    [closeSnackbar, enqueueSnackbar, prossessedResponse, t]
  );

  const updateProfile = useCallback(
    async (params, handleFormClose, translateFunc) => {
      try {
        const tr = translateFunc || t;
        const response = await axios.post('/api/users/profile', { user: params });
        if ([200, 201].includes(response.status)) {
          if (typeof handleFormClose === 'function') {
            handleFormClose();
          }
          enqueueSnackbar(tr('APIcompound.profileUpdateSuccess'), {
            variant: 'success',
            action: (key) => (
              <IconButtonAnimate size="small" onClick={() => closeSnackbar(key)}>
                <Iconify icon={'eva:close-fill'} />
              </IconButtonAnimate>
            )
          });

          setTimeout(async () => {
            await logOut();
          }, 6000);
        }
      } catch (error) {
        console.warn('Update profile error:', error);
        prossessedResponse({ status: 401, message: t('APIcompound.unauthorized'), code: 'warning' });
      }
    },
    [closeSnackbar, enqueueSnackbar, logOut, prossessedResponse, t]
  );

  const memoizedValue = useMemo(
    () => ({
      ...state,
      logIn,
      logOut,
      register,
      confirm,
      passwordResetRequest,
      validateToken,
      updateProfile,
      updatePassword,
      prossessedResponse
    }),
    [
      state,
      logIn,
      logOut,
      register,
      confirm,
      passwordResetRequest,
      validateToken,
      updateProfile,
      updatePassword,
      prossessedResponse
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

export { AuthContext, AuthProvider };
